import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { pageRepository, createPagePort, publishPagePort } from '@/lib/container'
import { useAuthStore } from '@/store/authStore'
import type { CreatePageDTO } from '@/application/dto'
import type { PageMeta } from '@brix/types'

export function usePage(pageId: string) {
  return useQuery({
    queryKey: ['page', pageId],
    queryFn: () => pageRepository.findById(pageId),
    enabled: !!pageId,
  })
}

export function useUpdatePage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string; title?: string; slug?: string; meta?: PageMeta }) =>
      pageRepository.update(id, patch),
    onSuccess: (page) => {
      void qc.invalidateQueries({ queryKey: ['page', page.id] })
      void qc.invalidateQueries({ queryKey: ['pages', page.siteId] })
    },
  })
}

export function usePages(siteId: string) {
  return useQuery({
    queryKey: ['pages', siteId],
    queryFn: () => pageRepository.findBySiteId(siteId),
    enabled: !!siteId,
  })
}

export function useCreatePage(siteId: string) {
  const qc = useQueryClient()
  const tenant = useAuthStore((s) => s.tenant)

  return useMutation({
    mutationFn: (dto: CreatePageDTO) =>
      createPagePort.execute({ tenantId: tenant!.id, siteId, ...dto }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pages', siteId] }),
  })
}

// Alias that exposes the created page (same as useCreatePage, Page is returned by mutateAsync)
export { useCreatePage as useCreatePageWithResult }

export function useDeletePage(siteId: string) {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => pageRepository.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pages', siteId] }),
  })
}

export function usePublishPage(siteId: string) {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: ({ pageId, publish }: { pageId: string; publish: boolean }) =>
      publishPagePort.execute(pageId, publish),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pages', siteId] }),
  })
}

export function usePageBlocks(pageId: string) {
  return useQuery({
    queryKey: ['blocks', pageId],
    queryFn: () => pageRepository.getBlocks(pageId),
    enabled: !!pageId,
  })
}
